import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  MonitorPlay, Calendar, Check, Clock, Users, 
  Zap, BarChart3, Smartphone, Globe, Shield, 
  ArrowRight, Sparkles, Eye, Lock, Bot
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const MeetingRoomPage = () => {
  const features = [
    {
      icon: Calendar,
      title: 'Reservas Ad-Hoc',
      description: 'Reserva salas directamente desde la pantalla táctil en segundos. Sistema intuitivo con señales de colores (verde/naranja/rojo).'
    },
    {
      icon: Bot,
      title: 'IA para Liberación Automática',
      description: 'Detecta y libera automáticamente reservas fantasma o no utilizadas para optimizar el uso de espacios.'
    },
    {
      icon: Eye,
      title: 'Monitoreo en Tiempo Real',
      description: 'Supervisa el estado de todas tus salas desde un dashboard centralizado con analytics avanzado.'
    },
    {
      icon: Check,
      title: 'Confirmación de Llegada',
      description: 'Sistema de check-in que cancela automáticamente reservas no confirmadas, liberando espacios inmediatamente.'
    },
    {
      icon: Globe,
      title: 'Sincronización Múltiple',
      description: 'Integración nativa con Google Workspace, Microsoft 365, Exchange y calendarios de terceros.'
    },
    {
      icon: Sparkles,
      title: 'Personalización Total',
      description: 'Adapta el diseño, colores, logo y contenido de cada pantalla según tu identidad corporativa.'
    }
  ];

  const integrations = [
    {
      title: 'Control de Acceso',
      description: 'Integra con sistemas de acceso para automatizar puertas y torniquetes',
      icon: Lock
    },
    {
      title: 'Sensores de Ocupación',
      description: 'Detección inteligente de presencia para verificar uso real de espacios',
      icon: Eye
    },
    {
      title: 'Aplicación Móvil',
      description: 'Reserva desde cualquier lugar con app iOS y Android',
      icon: Smartphone
    },
    {
      title: 'APIs Abiertas',
      description: 'Conecta con cualquier sistema mediante nuestra API REST',
      icon: Zap
    }
  ];

  const benefits = [
    {
      stat: '40%',
      description: 'Reducción en salas infrautilizadas'
    },
    {
      stat: '200+',
      description: 'Espacios automatizados'
    },
    {
      stat: '99.9%',
      description: 'Uptime garantizado'
    },
    {
      stat: '5 min',
      description: 'Tiempo de configuración'
    }
  ];

  const useCases = [
    {
      title: 'Coworks',
      items: [
        'Salas de reuniones',
        'Cabinas privadas',
        'Espacios de eventos',
        'Salas de videoconferencia'
      ]
    },
    {
      title: 'Empresas',
      items: [
        'Salas de juntas',
        'Espacios de colaboración',
        'Auditorios',
        'Training rooms'
      ]
    },
    {
      title: 'Inmobiliarias',
      items: [
        'Salas de ventas',
        'Amenities de edificios',
        'Espacios comunes',
        'Salas de eventos'
      ]
    },
    {
      title: 'Multifamilias',
      items: [
        'Salón de eventos',
        'Sala de cine',
        'Gimnasio',
        'Cowork del edificio'
      ]
    }
  ];

  const hardwareOptions = [
    {
      name: 'Tablets Android/iOS',
      description: 'Convierte cualquier tablet en pantalla de sala',
      price: 'Desde tu dispositivo'
    },
    {
      name: 'Pantallas Táctiles 10"',
      description: 'Pantallas profesionales montadas en pared',
      price: 'Consultar precio'
    },
    {
      name: 'Displays Grandes',
      description: 'Paneles informativos para navegación de espacios',
      price: 'Consultar precio'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-black relative overflow-hidden min-h-screen flex items-center">
        {/* Video background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://erp-back.nyc3.digitaloceanspaces.com/assets_public/mroom_1.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay with transparency */}
        <div className="absolute inset-0 bg-black/70"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
              MeetingRoom Display
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
              Visualiza Disponibilidad en Tiempo Real
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
              Gestión inteligente de salas de reuniones con IA. Elimina las reservas fantasma, optimiza tus espacios y mejora la productividad de tu organización.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Agendar Demostración
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.open('https://play.google.com/store/apps/details?id=com.cloudbase.roomDisplay&hl=es_419', '_blank')}
              >
                Descargar App Gratis
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>App gratuita disponible</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Configuración en 5 minutos</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Integraciones incluidas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Stats */}
      <section className="py-20 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-gray-900 mb-2">{benefit.stat}</div>
                <div className="text-gray-600">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Cómo Funciona
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Tres pasos simples para optimizar la gestión de tus espacios
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                  1
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Reserva</h3>
                <p className="text-gray-600 leading-relaxed">
                  Reserva desde la pantalla táctil, tu calendario (Outlook/Google) o la app móvil. Sistema intuitivo con confirmación instantánea.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                  2
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Monitorea</h3>
                <p className="text-gray-600 leading-relaxed">
                  La IA supervisa el uso real. Detecta reservas no utilizadas y las libera automáticamente, maximizando la disponibilidad.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                  3
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Optimiza</h3>
                <p className="text-gray-600 leading-relaxed">
                  Analiza patrones de uso con dashboards en tiempo real. Toma decisiones basadas en datos para mejorar tus espacios.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Características Principales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Todo lo que necesitas para gestionar tus espacios de manera eficiente
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-8">
                    <Icon className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Integra con tu Ecosistema
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Conecta MeetingRoom con todos tus sistemas para una automatización completa
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {integrations.map((integration, index) => {
              const Icon = integration.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {integration.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {integration.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Casos de Uso
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              MeetingRoom se adapta a cualquier tipo de espacio en tu organización
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <Card key={index} className="border border-gray-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{useCase.title}</h3>
                  <ul className="space-y-3">
                    {useCase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hardware Options */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Hardware Flexible
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Usa tu propio dispositivo o elige de nuestra selección de hardware profesional
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {hardwareOptions.map((option, index) => (
              <Card key={index} className="border border-gray-200 text-center">
                <CardContent className="p-8">
                  <MonitorPlay className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {option.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {option.description}
                  </p>
                  <p className="text-green-600 font-semibold">
                    {option.price}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white px-10 py-6 text-base rounded font-semibold"
              onClick={() => window.open('https://play.google.com/store/apps/details?id=com.cloudbase.roomDisplay&hl=es_419', '_blank')}
            >
              Descargar App Gratuita para Android
            </Button>
            <p className="mt-4 text-gray-600 text-sm">
              Convierte tu tablet en pantalla de sala en minutos
            </p>
          </div>
        </div>
      </section>

      {/* Trust & Security */}
      <section className="py-20 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Seguridad Enterprise</h3>
                <p className="text-gray-600 text-sm">Encriptación bancaria y cumplimiento ISO 27001</p>
              </div>
              <div>
                <Globe className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-idioma</h3>
                <p className="text-gray-600 text-sm">Disponible en 24 idiomas incluyendo español</p>
              </div>
              <div>
                <BarChart3 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Analytics Incluido</h3>
                <p className="text-gray-600 text-sm">Dashboard con métricas y reportes en tiempo real</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl sm:text-6xl font-bold text-white mb-8">
              Transforma tus Espacios Hoy
            </h2>
            <p className="text-xl text-gray-300 mb-12 font-light">
              Más de 200 espacios ya optimizan su gestión con MeetingRoom. Únete a ellos.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
                onClick={() => window.location.href = '/demo'}
              >
                Agendar Demostración
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Contactar Ventas
              </Button>
            </div>
            
            <p className="mt-8 text-gray-400 text-sm">
              Configuración gratuita incluida • Soporte técnico 24/7 • Sin compromisos
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MeetingRoomPage;
